/**
 * Real API Service Layer for Electricity Billing System
 * Integrates with MongoDB backend via platform APIs
 * Handles UTC timestamps and flexible cost categories
 */
/* eslint-disable max-classes-per-file, no-plusplus, no-await-in-loop, no-restricted-syntax, max-len, no-restricted-globals, no-continue */

import SessionService from '@shopworx/services/util/session.service';
import ApiService from '@shopworx/services/api/api.service';
import {
  CostCategory,
  RateMaster,
  BillingRecord,
  ConsumptionData,
  DGBillingRecord,
  DGConsumptionData,
} from '../types';
import appConfig from '../config/appConfig';
import { resolveBillingMode, isTimeOfUseBillingMode, isStandardTariffBillingMode } from '../utils/billingMode';
import { calculateTimeOfUseBill } from '../utils/timeOfUseBilling';
import { calculateStandardTariffBill } from '../utils/standardTariffBilling';
import {
  TIME_OF_USE_DEMAND_CODES,
  TIME_OF_USE_UNIT_CODES,
  normalizePercentageAppliesTo,
} from '../utils/chargeMaster';

const TIME_OF_USE_ONLY_CHARGE_CODES = new Set([
  ...TIME_OF_USE_UNIT_CODES,
  ...TIME_OF_USE_DEMAND_CODES,
  'OIL_SURCHARGE',
  '',
  'BILLING_UNIT_COST',
  '',
]);

// API Configuration
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  const configuredBaseUrl = appConfig.getApiBaseUrl();
  if (configuredBaseUrl) {
    return configuredBaseUrl;
  }

  return '';
};

const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  ENDPOINTS: {
    BILLING_RATES: '/server/elements/billing_rates/records',
    COST_CATEGORIES: '/server/elements/cost_categories/records',
    BILLING_APP_CONFIG: '/server/elements/billing_app_config/records',
    BUSINESS_HOURS: '/server/elements/businesshours/records',
    BILLING_RECORDS: '/server/elements/billing_records/records',
    CONSUMPTION_READINGS: '/server/elements/consumption_readings/records',
    CONSUMPTION_SUMMARY: '/server/elements/consumption_summary/records',
    // DG Billing endpoints
    DG_CONSUMPTION_READINGS: '/server/elements/dg_consumption_readings/records',
    DG_CONSUMPTION_SUMMARY: '/server/elements/dg_consumption_summary/records',
    DG_BILLING_RECORDS: '/server/elements/dg_billing_records/records',
  },
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

const BILLING_RECORD_API_FIELDS = new Set([
  'assetid',
  'asset_id',
  'billing_period_start',
  'billing_period_end',
  'total_consumption',
  'initial_reading',
  'final_reading',
  'tou_breakdown',
  'demand_breakdown',
  'kva_demand',
  'kva_demand_charges',
  'charge_breakdown',
  'actual_bill_amount',
  'actual_kwh_from_bill',
  'oil_surcharge_rate',
  'service_charge',
  'unit_cost_for_billing',
  'calculation_mode',
  'variance',
  'rates_used',
  'notes',
  'created_by',
  'created_at',
  'updated_at',
  'is_active',
]);

const sanitizeApiPayload = (payload = {}, allowedFields = new Set()) => Object.fromEntries(
  Object.entries(payload)
    .filter(([key]) => allowedFields.has(key)),
);

const getPercentageAppliesTo = (category = {}) => {
  const appliesTo = category.percentage_applies_to || category.percentageAppliesTo || [];
  return normalizePercentageAppliesTo(appliesTo);
};

const getRateCategoryCode = (rate = {}) => (
  rate.cost_category
  || rate.costCategory
  || rate.category_code
  || rate.categoryCode
  || ''
);

const getSessionHeaders = () => {
  const sessionId = SessionService.getSession();
  return sessionId ? { sessionId } : {};
};

/**
 * Billing API Service
 * Handles API requests with retry logic and error handling
 */
class BillingApiService {
  static getRequestConfig(options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getSessionHeaders(),
      ...options.headers,
    };

    return {
      ...options,
      headers,
      timeout: options.timeout || API_CONFIG.TIMEOUT,
    };
  }

  static getResponseData(response) {
    return response?.data ?? response;
  }

  static getErrorMessage(error) {
    if (error?.response) {
      const responseData = typeof error.response.data === 'string'
        ? error.response.data
        : JSON.stringify(error.response.data || {});

      return `HTTP ${error.response.status}: ${error.response.statusText || responseData}`;
    }

    return error?.message || 'Unknown API error';
  }

  /**
   * Makes HTTP request with retry logic
   * @param {string} url - Request URL
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  static async request(url, options = {}) {
    const method = (options.method || 'GET').toUpperCase();
    const data = options.data || options.body || {};
    const config = this.getRequestConfig(options);

    let lastError;
    for (let attempt = 1; attempt <= API_CONFIG.RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await ApiService.getAxiosInstance()({
          ...config,
          method,
          url,
          data,
        });
        return this.getResponseData(response);
      } catch (error) {
        lastError = error;
        console.warn(`API request attempt ${attempt} failed:`, this.getErrorMessage(error));

        if (attempt < API_CONFIG.RETRY_ATTEMPTS) {
          await this.delay(API_CONFIG.RETRY_DELAY * attempt);
        }
      }
    }

    throw new Error(`API request failed after ${API_CONFIG.RETRY_ATTEMPTS} attempts: ${this.getErrorMessage(lastError)}`);
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} query - Query parameters
   * @returns {Promise<Object>} Response data
   */
  static async get(endpoint, query = {}) {
    const url = new URL(`${getApiBaseUrl()}${endpoint}`);

    // Add query parameters in the format: key>=value&&key<=value
    if (Object.keys(query).length > 0) {
      const queryParts = [];

      for (const [key, value] of Object.entries(query)) {
        if (typeof value === 'object' && value !== null) {
          // Handle MongoDB operators like { $gte: 123, $lte: 456 }
          for (const [operator, operatorValue] of Object.entries(value)) {
            if (operator === '$gte') {
              queryParts.push(`${key}>=${operatorValue}`);
            } else if (operator === '$lte') {
              queryParts.push(`${key}<=${operatorValue}`);
            } else if (operator === '$gt') {
              queryParts.push(`${key}>${operatorValue}`);
            } else if (operator === '$lt') {
              queryParts.push(`${key}<${operatorValue}`);
            } else if (operator === '$eq') {
              queryParts.push(`${key}==${operatorValue}`);
            } else if (operator === '$ne') {
              queryParts.push(`${key}!=${operatorValue}`);
            }
          }
        } else if (typeof value === 'string') {
          // For string values: key=="value"
          queryParts.push(`${key}=="${value}"`);
        } else {
          // For other types (boolean, number): key==value
          queryParts.push(`${key}==${value}`);
        }
      }

      if (queryParts.length > 0) {
        url.searchParams.append('query', queryParts.join('&&'));
      }
    }

    return this.request(url.toString());
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} Response data
   */
  static async post(endpoint, data) {
    const url = new URL(`${getApiBaseUrl()}${endpoint}`);

    return this.request(url.toString(), {
      method: 'POST',
      data,
    });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} Response data
   */
  static async put(endpoint, data) {
    const url = new URL(`${getApiBaseUrl()}${endpoint}`);

    return this.request(url.toString(), {
      method: 'PUT',
      data,
    });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Response data
   */
  static async delete(endpoint) {
    const url = new URL(`${getApiBaseUrl()}${endpoint}`);

    return this.request(url.toString(), {
      method: 'DELETE',
    });
  }

  /**
   * Utility delay function
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  static delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Date Utility for UTC timestamp handling
 */
class DateUtils {
  static getConfiguredTimezoneOffsetMinutes() {
    const offsetMinutes = typeof appConfig.getTimezoneOffsetMinutes === 'function'
      ? appConfig.getTimezoneOffsetMinutes()
      : null;

    return typeof offsetMinutes === 'number' ? offsetMinutes : null;
  }

  static getDateParts(date) {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map((value) => parseInt(value, 10));
      return { year, month, day };
    }

    const parsedDate = new Date(date);
    return {
      year: parsedDate.getFullYear(),
      month: parsedDate.getMonth() + 1,
      day: parsedDate.getDate(),
    };
  }

  /**
   * Converts Date to UTC timestamp (milliseconds)
   * IMPORTANT: This method matches the backend's date storage format
   * The backend stores local midnight times as UTC timestamps
   * @param {Date|string} date - Date to convert
   * @returns {number} UTC timestamp in milliseconds (matching backend format)
   */
  static toUTCTimestamp(date) {
    const offsetMinutes = this.getConfiguredTimezoneOffsetMinutes();
    if (offsetMinutes !== null) {
      const { year, month, day } = this.getDateParts(date);
      return Date.UTC(year, month - 1, day, 0, 0, 0, 0) - (offsetMinutes * 60 * 1000);
    }

    // If the date is a string like "2024-01-01", create local midnight and get timestamp
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Create date in local timezone at midnight, then get UTC timestamp
      // This matches how the backend stores dates
      const localDate = new Date(`${date}T00:00:00`);
      return localDate.getTime();
    }

    // For Date objects (from date pickers), create local midnight timestamp
    const d = new Date(date);

    // Create a new date with the same year/month/day but at local midnight
    const localMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

    // Return the timestamp (which represents local midnight as UTC)
    // This matches the backend's storage format
    return localMidnight.getTime();
  }

  /**
   * Converts UTC timestamp to Date object
   * @param {number} timestamp - UTC timestamp in milliseconds
   * @returns {Date} Date object
   */
  static fromUTCTimestamp(timestamp) {
    return new Date(timestamp);
  }

  /**
   * Converts a date to compact YYYYMMDD format for rate effective dates.
   * @param {Date|string|number} date - Date to convert
   * @returns {number} Date as YYYYMMDD
   */
  static toYYYYMMDD(date) {
    if (typeof date === 'number' && date >= 10000101 && date <= 99991231) {
      return date;
    }

    if (typeof date === 'string' && /^\d{8}$/.test(date)) {
      return parseInt(date, 10);
    }

    const { year, month, day } = this.getDateParts(date);
    const yearPart = year * 10000;
    const monthPart = month * 100;
    return yearPart + monthPart + day;
  }

  /**
   * Converts compact YYYYMMDD values from API to a Date object.
   * Falls back to timestamp parsing for older records.
   * @param {number|string|Date} value - API date value
   * @returns {Date} Date object
   */
  static fromYYYYMMDD(value) {
    if (value instanceof Date) {
      return value;
    }

    const rawValue = typeof value === 'string' ? value.trim() : value;
    if (/^\d{8}$/.test(String(rawValue))) {
      const compactDate = parseInt(rawValue, 10);
      const year = Math.floor(compactDate / 10000);
      const month = Math.floor((compactDate % 10000) / 100);
      const day = compactDate % 100;
      return new Date(year, month - 1, day);
    }

    return DateUtils.fromUTCTimestamp(value);
  }

  /**
   * Converts Date to ISO string for API
   * @param {Date|string} date - Date to convert
   * @returns {string} ISO date string
   */
  static toISOString(date) {
    return new Date(date).toISOString();
  }

  /**
   * Gets current UTC timestamp
   * @returns {number} Current UTC timestamp in milliseconds
   */
  static now() {
    return Date.now();
  }

  /**
   * Converts Date to UTC timestamp for end of day (23:59:59.999)
   * IMPORTANT: This method matches the backend's date storage format
   * @param {Date|string} date - Date to convert
   * @returns {number} UTC timestamp in milliseconds (end of day, matching backend format)
   */
  static toUTCTimestampEndOfDay(date) {
    const offsetMinutes = this.getConfiguredTimezoneOffsetMinutes();
    if (offsetMinutes !== null) {
      const { year, month, day } = this.getDateParts(date);
      return Date.UTC(year, month - 1, day, 23, 59, 59, 999) - (offsetMinutes * 60 * 1000);
    }

    // If the date is a string like "2024-01-01", create local end of day and get timestamp
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Create date in local timezone at end of day, then get UTC timestamp
      const localDate = new Date(`${date}T23:59:59.999`);
      return localDate.getTime();
    }

    // For Date objects, create local end of day timestamp
    const d = new Date(date);

    // Create a new date with the same year/month/day but at local end of day
    const localEndOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    // Return the timestamp (which represents local end of day as UTC)
    return localEndOfDay.getTime();
  }

  /**
   * Creates a UTC date from year, month, day
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @param {number} day - Day
   * @returns {Date} UTC Date object
   */
  static createUTCDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  }
}

const mapRateMaster = (rate = {}) => new RateMaster({
  ...rate,
  _id: rate._id,
  id: rate.id || rate._id,
  costCategory: getRateCategoryCode(rate),
  rateType: rate.rate_type || rate.rateType,
  effectiveFrom: DateUtils.fromYYYYMMDD(rate.effective_from || rate.effectiveFrom),
  effectiveTo: DateUtils.fromYYYYMMDD(rate.effective_to || rate.effectiveTo),
  createdAt: rate.created_at
    ? DateUtils.fromUTCTimestamp(rate.created_at)
    : rate.createdAt,
  updatedAt: rate.updated_at
    ? DateUtils.fromUTCTimestamp(rate.updated_at)
    : rate.updatedAt,
  createdBy: rate.created_by || rate.createdBy,
  isActive: rate.is_active !== undefined ? rate.is_active : rate.isActive,
});

/**
 * Cost Categories API Service
 * Manages dynamic cost categories
 */
export class CostCategoriesService {
  /**
   * Gets all cost categories
   * @returns {Promise<Array>} Array of cost categories
   */
  static async getAllCategories() {
    try {
      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.COST_CATEGORIES);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const categories = response.results || response.data || response;

      return categories;
    } catch (error) {
      console.error('Error fetching cost categories:', error);
      throw error;
    }
  }

  /**
   * Gets active cost categories ordered by calculation_order
   * @returns {Promise<Array>} Array of active cost categories
   */
  static async getActiveCategories() {
    try {
      const query = { is_active: true };
      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.COST_CATEGORIES, query);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const categories = response.results || response.data || response;

      // Sort by calculation_order
      return categories.sort((a, b) => a.calculation_order - b.calculation_order);
    } catch (error) {
      console.error('Error fetching active cost categories:', error);
      throw error;
    }
  }

  /**
   * Creates a new cost category
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category
   */
  static async createCategory(categoryData) {
    try {
      const data = {
        ...categoryData,
        created_at: DateUtils.now(),
        updated_at: DateUtils.now(),
      };

      const response = await BillingApiService.post(API_CONFIG.ENDPOINTS.COST_CATEGORIES, data);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      return response.results || response.data || response;
    } catch (error) {
      console.error('Error creating cost category:', error);
      throw error;
    }
  }

  /**
   * Updates an existing cost category
   * @param {string} id - Category ID (can be either id or _id)
   * @param {Object} updateData - Updated category data
   * @returns {Promise<Object>} Updated category
   */
  static async updateCategory(id, updateData) {
    try {
      const data = {
        ...updateData,
        assetid: 0,
        updated_at: DateUtils.now(),
      };

      // Use _id for the endpoint if available, otherwise use id
      const objectId = updateData._id || id;
      const endpoint = `${API_CONFIG.ENDPOINTS.COST_CATEGORIES}/${objectId}`;

      const response = await BillingApiService.put(endpoint, data);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const result = response.results || response.data || response;

      return result;
    } catch (error) {
      console.error('❌ Error updating cost category:', error);
      throw error;
    }
  }

  /**
   * Gets a single cost category by ID
   * @param {string} id - Category ID (MongoDB ObjectId)
   * @returns {Promise<Object|null>} Category or null if not found
   */
  static async getCategoryById(id) {
    try {
      // Use the specific record endpoint: /server/elements/cost_categories/records/{id}
      const endpoint = `${API_CONFIG.ENDPOINTS.COST_CATEGORIES}/${id}`;
      const response = await BillingApiService.get(endpoint);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const category = response.results || response.data || response;

      return category || null;
    } catch (error) {
      console.error('Error fetching cost category by ID:', error);
      throw error;
    }
  }

  /**
   * Deletes a cost category (marks as inactive)
   * @param {string} id - Category ID
   * @returns {Promise<boolean>} Success status
   */
  static async deleteCategory(id) {
    try {
      const data = {
        is_active: false,
        updated_at: DateUtils.now(),
      };

      const endpoint = `${API_CONFIG.ENDPOINTS.COST_CATEGORIES}/${id}`;
      await BillingApiService.put(endpoint, data);
      return true;
    } catch (error) {
      console.error('Error deleting cost category:', error);
      throw error;
    }
  }

  /**
   * Permanently deletes a cost category
   * @param {string} id - Category ID
   * @returns {Promise<boolean>} Success status
   */
  static async hardDeleteCategory(id) {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.COST_CATEGORIES}/${id}`;
      await BillingApiService.delete(endpoint);
      return true;
    } catch (error) {
      console.error('Error deleting cost category:', error);
      throw error;
    }
  }
}

/**
 * Business Hours API Service
 * Reads site-level schedule metadata such as timezone.
 */
export class RealBusinessHoursService {
  static async getTimezone() {
    const url = new URL(`${getApiBaseUrl()}${API_CONFIG.ENDPOINTS.BUSINESS_HOURS}`);

    url.searchParams.append('sortquery', 'sortindex==1');

    const response = await BillingApiService.request(url.toString());
    const records = response.results || response.data || response;
    const businessHoursRecords = Array.isArray(records) ? records : [];
    const timezoneRecord = businessHoursRecords.find((record) => record?.tz) || null;

    return timezoneRecord?.tz || null;
  }

  static async applyTimezoneConfig() {
    const timezone = await this.getTimezone();

    if (!timezone) {
      return null;
    }

    appConfig.setTimezone(timezone, 'businesshours');
    return timezone;
  }
}

/**
 * Billing App Configuration API Service
 * Handles editable billing app configuration stored in master data
 */
export class RealBillingAppConfigService {
  static transformFromAPI(record = {}) {
    return {
      _id: record._id,
      id: record.id || record._id,
      configKey: record.config_key || 'default',
      currencyCode: record.currency_code || appConfig.getCurrencyCode(),
      currencySymbol: record.currency_symbol || appConfig.getCurrencySymbol(),
      currencyDecimalPlaces: parseInt(record.currency_decimal_places, 10) || 2,
      timeOfUse: record.time_of_use === true,
      splitDemandByTouPeriod: record.split_demand_by_tou_period === true,
      useTouUnitCost: record.use_tou_unit_cost !== false,
      isActive: record.is_active !== false,
      createdAt: record.created_at ? DateUtils.fromUTCTimestamp(record.created_at) : null,
      updatedAt: record.updated_at ? DateUtils.fromUTCTimestamp(record.updated_at) : null,
    };
  }

  static transformToAPI(config = {}) {
    return {
      config_key: config.configKey || 'default',
      currency_code: config.currencyCode || appConfig.getCurrencyCode(),
      currency_symbol: config.currencySymbol || appConfig.getCurrencySymbol(),
      currency_decimal_places: parseInt(config.currencyDecimalPlaces, 10) || 2,
      time_of_use: config.timeOfUse === true,
      split_demand_by_tou_period: config.splitDemandByTouPeriod === true,
      use_tou_unit_cost: config.useTouUnitCost !== false,
      is_active: config.isActive !== false,
    };
  }

  static async getActiveConfig() {
    const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_APP_CONFIG, {
      is_active: true,
    });
    const records = response.results || response.data || response;
    const configRecords = Array.isArray(records) ? records : [];
    const config = configRecords.find((record) => record.config_key === 'default')
      || configRecords[0]
      || null;

    return config ? this.transformFromAPI(config) : null;
  }

  static async saveConfig(config) {
    const data = {
      ...this.transformToAPI(config),
      assetid: 0,
      updated_at: DateUtils.now(),
    };

    if (config._id || config.id) {
      const objectId = config._id || config.id;
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_APP_CONFIG}/${objectId}`;
      await BillingApiService.put(endpoint, data);
      return this.getActiveConfig();
    }

    const response = await BillingApiService.post(API_CONFIG.ENDPOINTS.BILLING_APP_CONFIG, {
      ...data,
      created_at: DateUtils.now(),
    });
    const createdConfig = response.results || response.data || response;
    return this.transformFromAPI(createdConfig);
  }
}

/**
 * Rate Master API Service
 * Handles CRUD operations for billing rates with real API
 */
export class RealRateMasterService {
  /**
   * Gets all rates from API
   * @returns {Promise<RateMaster[]>} Array of rate master objects
   */
  static async getAllRates() {
    try {
      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_RATES);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const rates = response.results || response.data || response;

      return rates.map(mapRateMaster);
    } catch (error) {
      console.error('Error fetching rates:', error);
      throw error;
    }
  }

  /**
   * Gets rates by cost category
   * @param {string} costCategory - Cost category to filter by
   * @returns {Promise<RateMaster[]>} Array of matching rates
   */
  static async getRatesByCategory(costCategory) {
    try {
      const query = { cost_category: costCategory, is_active: true };
      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_RATES, query);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const rates = response.results || response.data || response;

      return rates.map(mapRateMaster);
    } catch (error) {
      console.error('Error fetching rates by category:', error);
      throw error;
    }
  }

  /**
   * Gets the effective rate for a specific category and date
   * @param {string} costCategory - Cost category
   * @param {Date} date - Date to check effectiveness
   * @returns {Promise<RateMaster|null>} Effective rate or null if none found
   */
  static async getEffectiveRate(costCategory, date) {
    try {
      const effectiveDate = DateUtils.toYYYYMMDD(date);
      const query = {
        cost_category: costCategory,
        is_active: true,
        effective_from: { $lte: effectiveDate },
        effective_to: { $gte: effectiveDate },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_RATES, query);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const rates = response.results || response.data || response;

      if (rates.length === 0) return null;

      const toEffectiveDate = (rate) => {
        const value = rate.effective_from || rate.effectiveFrom;
        if (!value) return 0;
        if (/^\d{8}$/.test(String(value))) return parseInt(value, 10);
        return DateUtils.toYYYYMMDD(value);
      };
      const rate = [...rates].sort((a, b) => (
        toEffectiveDate(b) - toEffectiveDate(a)
      ))[0];
      return mapRateMaster(rate);
    } catch (error) {
      console.error('Error fetching effective rate:', error);
      throw error;
    }
  }

  /**
   * Creates a new rate
   * @param {Object} rateData - Rate data
   * @returns {Promise<RateMaster>} Created rate
   */
  static async createRate(rateData) {
    try {
      const data = {
        cost_category: rateData.costCategory,
        rate: rateData.rate,
        rate_type: rateData.rateType,
        effective_from: DateUtils.toYYYYMMDD(rateData.effectiveFrom),
        effective_to: DateUtils.toYYYYMMDD(rateData.effectiveTo),
        created_by: rateData.createdBy,
        created_at: DateUtils.now(),
        updated_at: DateUtils.now(),
        is_active: rateData.isActive !== false,
      };

      const response = await BillingApiService.post(API_CONFIG.ENDPOINTS.BILLING_RATES, data);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const createdRate = response.results || response.data || response;

      return mapRateMaster(createdRate);
    } catch (error) {
      console.error('Error creating rate:', error);
      throw error;
    }
  }

  /**
   * Updates an existing rate
   * @param {string} id - Rate ID (can be either id or _id)
   * @param {Object} updateData - Updated rate data
   * @returns {Promise<RateMaster>} Updated rate
   */
  static async updateRate(id, updateData) {
    try {
      const data = {
        assetid: 0,
        updated_at: DateUtils.now(),
      };

      if (updateData.costCategory) {
        data.cost_category = updateData.costCategory;
      }
      if (updateData.rate !== undefined) {
        data.rate = updateData.rate;
      }
      if (updateData.rateType) {
        data.rate_type = updateData.rateType;
      }
      if (updateData.effectiveFrom) {
        data.effective_from = DateUtils.toYYYYMMDD(updateData.effectiveFrom);
      }
      if (updateData.effectiveTo) {
        data.effective_to = DateUtils.toYYYYMMDD(updateData.effectiveTo);
      }
      if (updateData.createdBy && updateData.createdBy !== 'current_user') {
        data.created_by = updateData.createdBy;
      }
      if (updateData.isActive !== undefined) {
        data.is_active = updateData.isActive;
      }

      // Use _id for the endpoint if available, otherwise use id
      const objectId = updateData._id || id;
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RATES}/${objectId}`;
      await BillingApiService.put(endpoint, data);
      return RealRateMasterService.getRateById(objectId);
    } catch (error) {
      console.error('Error updating rate:', error);
      throw error;
    }
  }

  /**
   * Gets a single rate by ID
   * @param {string} id - Rate ID (MongoDB ObjectId)
   * @returns {Promise<RateMaster|null>} Rate or null if not found
   */
  static async getRateById(id) {
    try {
      // Use the specific record endpoint: /server/elements/billing_rates/records/{id}
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RATES}/${id}`;
      const response = await BillingApiService.get(endpoint);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const rate = response.results || response.data || response;

      if (!rate) return null;

      return mapRateMaster(rate);
    } catch (error) {
      console.error('Error fetching rate by ID:', error);
      throw error;
    }
  }

  /**
   * Deletes a rate (marks as inactive)
   * @param {string} id - Rate ID
   * @returns {Promise<boolean>} Success status
   */
  static async deleteRate(id) {
    try {
      if (!id) {
        throw new Error('Rate ID is required for delete');
      }

      const data = {
        assetid: 0,
        is_active: false,
        updated_at: DateUtils.now(),
      };

      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RATES}/${id}`;
      await BillingApiService.put(endpoint, data);
      return true;
    } catch (error) {
      console.error('Error deleting rate:', error);
      throw error;
    }
  }
}

/**
 * Billing Records API Service
 * Handles CRUD operations for billing records with real API
 */
export class RealBillingRecordsService {
  /**
   * Gets all active billing records from API
   * @returns {Promise<BillingRecord[]>} Array of active billing records
   */
  static async getAllRecords() {
    try {
      // Only fetch active records (is_active = true or undefined for backward compatibility)
      const query = {
        $or: [
          { is_active: true },
          { is_active: { $exists: false } }, // For records created before soft delete was implemented
        ],
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_RECORDS, query);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const records = response.results || response.data || response;

      return records.map((record) => this.transformFromAPI(record));
    } catch (error) {
      console.error('Error fetching billing records:', error);
      throw error;
    }
  }

  /**
   * Gets billing records within a date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<BillingRecord[]>} Array of matching records
   */
  static async getRecordsByDateRange(startDate, endDate) {
    try {
      const query = {
        billing_period_start: { $lte: DateUtils.toYYYYMMDD(endDate) },
        billing_period_end: { $gte: DateUtils.toYYYYMMDD(startDate) },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.BILLING_RECORDS, query);
      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const records = response.results || response.data || response;

      return records.map((record) => this.transformFromAPI(record));
    } catch (error) {
      console.error('Error fetching billing records by date range:', error);
      throw error;
    }
  }

  /**
   * Creates a new billing record
   * @param {Object} recordData - Billing record data
   * @returns {Promise<BillingRecord>} Created record
   */
  static async createRecord(recordData) {
    try {
      const data = sanitizeApiPayload(
        this.transformToAPI(recordData),
        BILLING_RECORD_API_FIELDS,
      );
      const response = await BillingApiService.post(API_CONFIG.ENDPOINTS.BILLING_RECORDS, data);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const createdRecord = response.results || response.data || response;

      return this.transformFromAPI(createdRecord);
    } catch (error) {
      console.error('Error creating billing rerecord:', error);
      throw error;
    }
  }

  /**
   * Updates an existing billing record
   * @param {string} id - Record ID
   * @param {Object} updateData - Updated record data
   * @returns {Promise<BillingRecord>} Updated record
   */
  static async updateRecord(id, updateData) {
    try {
      const data = sanitizeApiPayload(
        {
          ...this.transformToAPI(updateData),
          updated_at: DateUtils.now(),
        },
        BILLING_RECORD_API_FIELDS,
      );

      // Use _id for the endpoint if available, otherwise use id
      const objectId = updateData._id || id;
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RECORDS}/${objectId}`;
      const response = await BillingApiService.put(endpoint, data);
      const updatedRecord = response?.results || response?.data || response;

      if (this.isApiBillingRecord(updatedRecord)) {
        return this.transformFromAPI(updatedRecord);
      }

      return this.transformFromAPI({
        ...data,
        id: updateData.id || id,
        _id: objectId,
      });
    } catch (error) {
      console.error('Error updating billing record:', error);
      throw error;
    }
  }

  static isApiBillingRecord(record) {
    return !!(record
      && typeof record === 'object'
      && !Array.isArray(record)
      && record.billing_period_start !== undefined
      && record.billing_period_end !== undefined);
  }

  /**
   * Transforms API response to application format
   * @param {Object} apiRecord - Record from API
   * @returns {BillingRecord} Transformed record
   */
  static transformFromAPI(apiRecord) {
    // Parse charge_breakdown if it's a JSON string
    let calculatedCharges = {};
    if (apiRecord.charge_breakdown) {
      try {
        calculatedCharges = typeof apiRecord.charge_breakdown === 'string'
          ? JSON.parse(apiRecord.charge_breakdown)
          : apiRecord.charge_breakdown;
      } catch (error) {
        console.error('Error parsing charge_breakdown:', error);
        calculatedCharges = {};
      }
    }

    const apiServiceCharge = parseFloat(apiRecord.service_charge) || 0;
    if (apiServiceCharge > 0
        && calculatedCharges
        && calculatedCharges.SERVICE_CHARGE === undefined
        && calculatedCharges.serviceCharges === undefined) {
      // Preserve service charge inside the breakdown so subtotal calculations include it.
      calculatedCharges.SERVICE_CHARGE = apiServiceCharge;
    }

    // Parse rates_used if it's a JSON string
    let ratesUsed = [];
    if (apiRecord.rates_used) {
      try {
        ratesUsed = typeof apiRecord.rates_used === 'string'
          ? JSON.parse(apiRecord.rates_used)
          : apiRecord.rates_used;
      } catch (error) {
        console.error('Error parsing rates_used:', error);
        ratesUsed = [];
      }
    }

    // Handle both possible field names for total consumption
    const totalConsumption = parseFloat(
      apiRecord.total_consumption
      || apiRecord.totalConsumption
      || 0,
    );

    // Normalize consumption data to ensure consistent field names
    const normalizedConsumptionData = {
      totalConsumption,
      initialReading: parseFloat(apiRecord.initial_reading) || 0,
      finalReading: parseFloat(apiRecord.final_reading) || 0,
      hourlyReadings: [], // Will be loaded separately if needed
      touBreakdown: typeof apiRecord.tou_breakdown === 'string' ? JSON.parse(apiRecord.tou_breakdown || '{}') : (apiRecord.tou_breakdown || {}),
      demandBreakdown: typeof apiRecord.demand_breakdown === 'string' ? JSON.parse(apiRecord.demand_breakdown || '{}') : (apiRecord.demand_breakdown || {}),
      billingPeriod: {
        startDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_start),
        endDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_end),
      },
    };

    const transformedRecord = new BillingRecord({
      id: apiRecord.id,
      _id: apiRecord._id, // Preserve MongoDB ObjectId for updates
      billingPeriod: {
        startDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_start),
        endDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_end),
      },
      consumptionData: normalizedConsumptionData,
      kvaDemand: parseFloat(apiRecord.kva_demand) || 0,
      kvaDemandCharges: parseFloat(apiRecord.kva_demand_charges) || 0,
      calculatedCharges, // Now properly parsed from JSON string
      actualBillAmount: parseFloat(apiRecord.actual_bill_amount) || 0,
      actualKwhFromBill: parseFloat(apiRecord.actual_kwh_from_bill) || 0,
      variance: parseFloat(apiRecord.variance) || 0,
      ratesUsed, // Now properly parsed from JSON string
      createdBy: apiRecord.created_by,
      createdAt: DateUtils.fromUTCTimestamp(apiRecord.created_at),
    });

    transformedRecord.kvaDemandCharges = parseFloat(apiRecord.kva_demand_charges) || 0;
    transformedRecord.assetid = apiRecord.assetid ?? apiRecord.assetId ?? 0;
    transformedRecord.assetId = transformedRecord.assetid;
    transformedRecord.timeOfUseUnits = normalizedConsumptionData.touBreakdown;
    transformedRecord.timeOfUseDemand = normalizedConsumptionData.demandBreakdown;
    transformedRecord.oilSurchargeRate = parseFloat(apiRecord.oil_surcharge_rate) || 0;
    transformedRecord.serviceCharge = parseFloat(apiRecord.service_charge) || 0;
    transformedRecord.unitCostForBilling = parseFloat(apiRecord.unit_cost_for_billing) || 0;
    transformedRecord.calculationMode = apiRecord.calculation_mode
      || (Object.keys(normalizedConsumptionData.touBreakdown || {}).length > 0 ? 'TIME_OF_USE' : undefined);
    transformedRecord.notes = apiRecord.notes || '';
    transformedRecord.updatedAt = apiRecord.updated_at ? DateUtils.fromUTCTimestamp(apiRecord.updated_at) : null;
    return transformedRecord;
  }

  /**
   * Gets a single billing record by ID
   * @param {string} id - Record ID
   * @returns {Promise<BillingRecord|null>} Billing record or null if not found
   */
  static async getRecordById(id) {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RECORDS}/${id}`;
      const response = await BillingApiService.get(endpoint);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const record = response.results || response.data || response;

      if (!record) {
        return null;
      }
      return this.transformFromAPI(record);
    } catch (error) {
      console.error('Error fetching billing record by ID:', error);

      // Return null for 404 errors (record not found)
      if (error.message && error.message.includes('HTTP 404')) {
        return null;
      }

      throw error;
    }
  }

  /**
   * Deletes a billing record by ID (soft delete)
   * @param {string} recordId - Record ID to delete
   * @returns {Promise<boolean>} Success status
   */
  static async deleteRecord(recordId) {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.BILLING_RECORDS}/${recordId}`;

      await BillingApiService.delete(endpoint);
      return true;
    } catch (error) {
      console.error('---> Error deleting billing record:', error);
      throw error;
    }
  }

  /**
   * Transforms application format to API format
   * @param {Object} recordData - Application record data
   * @returns {Object} API format data
   */
  static transformToAPI(recordData) {
    const toDoubleValue = (value) => {
      const parsedValue = parseFloat(value);
      return Number.isNaN(parsedValue) ? 0 : parsedValue;
    };

    return {
      assetid: recordData.assetid ?? recordData.assetId ?? 0,
      billing_period_start: DateUtils.toYYYYMMDD(recordData.billingPeriod.startDate),
      billing_period_end: DateUtils.toYYYYMMDD(recordData.billingPeriod.endDate),
      total_consumption: toDoubleValue(recordData.consumptionData.totalConsumption),
      initial_reading: recordData.consumptionData.initialReading || 0,
      final_reading: recordData.consumptionData.finalReading || 0,
      tou_breakdown: recordData.consumptionData.touBreakdown
        || recordData.timeOfUseUnits
        || {},
      demand_breakdown: recordData.consumptionData.demandBreakdown
        || recordData.timeOfUseDemand
        || {},
      kva_demand: recordData.kvaDemand,
      kva_demand_charges: recordData.kvaDemandCharges || 0,
      charge_breakdown: JSON.stringify(recordData.calculatedCharges || {}),
      actual_bill_amount: recordData.actualBillAmount,
      actual_kwh_from_bill: toDoubleValue(recordData.actualKwhFromBill),
      oil_surcharge_rate: recordData.oilSurchargeRate || 0,
      service_charge: recordData.serviceCharge || recordData.calculatedCharges?.SERVICE_CHARGE || recordData.calculatedCharges?.serviceCharges || 0,
      unit_cost_for_billing: recordData.unitCostForBilling || 0,
      calculation_mode: recordData.calculationMode,
      variance: recordData.variance,
      rates_used: JSON.stringify(recordData.ratesUsed || []),
      notes: recordData.notes || '',
      created_by: recordData.createdBy,
      created_at: recordData.createdAt ? new Date(recordData.createdAt).getTime() : DateUtils.now(),
      is_active: true, // Ensure new records are active by default
    };
  }
}

/**
 * Real Billing Calculation Service
 * Handles bill calculations using real API rates
 */
export class RealBillingCalculationService {
  /**
   * Calculates complete bill for given consumption and kVA data
   * @param {ConsumptionData} consumptionData - Consumption data
   * @param {number} kvaDemand - kVA demand
   * @param {Date} billingDate - Date for rate lookup
   * @returns {Promise<Object>} Calculated bill breakdown
   */
  static async calculateBill(consumptionData, kvaDemand, billingDate = new Date(), options = {}) {
    try {
      // Validate inputs
      if (!consumptionData) {
        throw new Error('Consumption data is required');
      }

      // Check if totalConsumption exists and is valid - handle both field name formats
      const totalConsumption = parseFloat(
        consumptionData.totalConsumption
        || consumptionData.total_consumption
        || 0,
      );
      const resolvedBillingMode = resolveBillingMode({
        consumptionData,
        billData: {
          calculationMode: options.billingMode,
          timeOfUseUnits: options.tariffData?.unitLines,
        },
        configMode: options.configMode,
      });
      const isTimeOfUseMode = isTimeOfUseBillingMode(resolvedBillingMode)
        || options.tariffMode === 'TIME_OF_USE';
      const timeOfUseUnitLines = options.tariffData?.unitLines || consumptionData.touBreakdown || {};
      const timeOfUseTotalConsumption = Object.values(timeOfUseUnitLines)
        .reduce((sum, line) => sum + (parseFloat(line?.units) || 0), 0);
      const effectiveTotalConsumption = isTimeOfUseMode && timeOfUseTotalConsumption > 0
        ? timeOfUseTotalConsumption
        : totalConsumption;

      if (isNaN(effectiveTotalConsumption) || effectiveTotalConsumption <= 0) {
        console.error('❌ Invalid consumption data structure:', consumptionData);
        throw new Error(`Invalid consumption data: totalConsumption is ${consumptionData.totalConsumption || consumptionData.total_consumption || 'undefined'} (expected positive number)`);
      }

      const parsedKvaDemand = parseFloat(kvaDemand);
      if (isNaN(parsedKvaDemand) || parsedKvaDemand < 0) {
        console.error('❌ Invalid kVA demand:', kvaDemand);
        throw new Error(`Invalid kVA demand: ${kvaDemand} (expected positive number)`);
      }

      const loadedRates = Array.isArray(options.rates) && options.rates.length
        ? options.rates
        : null;
      const toEffectiveDate = (rate) => {
        const value = rate.effective_from || rate.effectiveFrom;
        if (!value) return 0;
        if (/^\d{8}$/.test(String(value))) return parseInt(value, 10);
        return DateUtils.toYYYYMMDD(value);
      };
      const effectiveDate = DateUtils.toYYYYMMDD(billingDate);
      const getLoadedEffectiveRate = (categoryCode) => {
        if (!loadedRates) return null;

        return [...loadedRates]
          .filter((rate) => {
            const costCategory = rate.costCategory || rate.cost_category;
            const active = rate.isActive !== false && rate.is_active !== false;
            const effectiveTo = rate.effective_to || rate.effectiveTo;
            return active
              && costCategory === categoryCode
              && toEffectiveDate(rate) <= effectiveDate
              && effectiveTo
              && DateUtils.toYYYYMMDD(effectiveTo) >= effectiveDate;
          })
          .sort((a, b) => toEffectiveDate(b) - toEffectiveDate(a))[0] || null;
      };

      // Get active cost categories to determine calculation order
      const categories = Array.isArray(options.activeCategories) && options.activeCategories.length
        ? options.activeCategories
        : await CostCategoriesService.getActiveCategories();
      const calculationCategories = isTimeOfUseMode
        ? categories
        : categories.filter((category) => (
          !TIME_OF_USE_ONLY_CHARGE_CODES.has(category.category_code)
        ));

      // Get effective rates for all categories
      const rates = {};
      const ratesUsed = [];

      for (const category of calculationCategories) {
        if (rates[category.category_code]) {
          continue;
        }

        const rate = getLoadedEffectiveRate(category.category_code)
          || await RealRateMasterService.getEffectiveRate(category.category_code, billingDate);
        if (rate) {
          rates[category.category_code] = rate;
          ratesUsed.push(rate);
        }
      }

      const getFallbackActiveRate = async (categoryCode) => {
        if (loadedRates) {
          return loadedRates
            .filter((rate) => (
              (rate.costCategory || rate.cost_category) === categoryCode
              && rate.isActive !== false
              && rate.is_active !== false
            ))
            .sort((a, b) => new Date(b.effectiveFrom || b.effective_from)
              - new Date(a.effectiveFrom || a.effective_from))[0] || null;
        }

        let categoryRates = await RealRateMasterService.getRatesByCategory(categoryCode);
        if (!categoryRates.length) {
          const allRates = await RealRateMasterService.getAllRates();
          categoryRates = allRates.filter((rate) => rate.costCategory === categoryCode);
        }

        return categoryRates
          .filter((rate) => rate && rate.isActive !== false)
          .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0] || null;
      };

      const requiredStandardBaseRateCodes = [
        'KWH_FIXED_CHARGE',
        'KVA_DEMAND_CHARGE',
        'KVA_PER_UNIT_CHARGE',
      ];

      if (!isTimeOfUseMode) {
        for (const category of calculationCategories) {
          const categoryCode = category.category_code;
          if (rates[categoryCode]) {
            continue;
          }

          try {
            const fallbackRate = await getFallbackActiveRate(categoryCode);
            if (!fallbackRate) {
              continue;
            }

            rates[categoryCode] = fallbackRate;
            ratesUsed.push(fallbackRate);
            console.warn(`⚠️ Using fallback active ${categoryCode} rate because no date-effective rate was found:`, fallbackRate);
          } catch (fallbackError) {
            console.warn(`⚠️ Failed to load fallback ${categoryCode} rate:`, fallbackError);
          }
        }
      }

      if (
        !rates.SERVICE_CHARGE
        && calculationCategories.some(
          (serviceCategory) => serviceCategory.category_code === CostCategory.SERVICE_CHARGE,
        )
      ) {
        try {
          const fallbackServiceRate = await getFallbackActiveRate(CostCategory.SERVICE_CHARGE);

          if (fallbackServiceRate) {
            rates.SERVICE_CHARGE = fallbackServiceRate;
            ratesUsed.push(fallbackServiceRate);
            console.warn('⚠️ Using fallback service charge rate because no date-effective rate was found:', fallbackServiceRate);
          }
        } catch (fallbackError) {
          console.warn('⚠️ Failed to load fallback service charge rate:', fallbackError);
        }
      }

      if (
        !isTimeOfUseMode
        && requiredStandardBaseRateCodes.every((categoryCode) => !rates[categoryCode])
      ) {
        throw new Error(
          'Standard billing needs an active effective rate for KWH_FIXED_CHARGE or KVA_DEMAND_CHARGE/KVA_PER_UNIT_CHARGE. Only fixed/service rates were found.',
        );
      }

      if (isTimeOfUseMode) {
        const getEffectiveRateValue = (...categoryCodes) => {
          const rate = categoryCodes.map((categoryCode) => rates[categoryCode]).find(Boolean);
          const value = parseFloat(rate?.rate);
          return Number.isNaN(value) ? null : value;
        };
        const applyLineRates = (lines = {}) => Object.fromEntries(
          Object.entries(lines).map(([key, line]) => {
            const rateValue = getEffectiveRateValue(
              ...[line?.categoryCode].filter(Boolean),
            );

            return [
              key,
              {
                ...line,
                rate: rateValue ?? (parseFloat(line?.rate) || 0),
              },
            ];
          }),
        );
        const unitLines = applyLineRates(
          options.tariffData?.unitLines || consumptionData.touBreakdown || {},
        );
        const demandLines = applyLineRates(
          options.tariffData?.demandLines || consumptionData.demandBreakdown || {},
        );
        const oilSurchargeRate = getEffectiveRateValue(
          'OIL_SURCHARGE',
        );
        const serviceCharge = getEffectiveRateValue('SERVICE_CHARGE');
        const hasOilSurcharge = calculationCategories.some(
          (category) => category.category_code === 'OIL_SURCHARGE',
        );
        const hasServiceCharge = calculationCategories.some(
          (category) => category.category_code === 'SERVICE_CHARGE',
        );
        const percentageCategories = calculationCategories
          .map((category) => {
            const categoryCode = category.category_code;
            const rate = rates[categoryCode];
            const rateType = String(rate?.rate_type || rate?.rateType || '').toUpperCase();
            const baseChargeValue = category.is_base_charge ?? category.isBaseCharge;
            const isBaseCharge = typeof baseChargeValue === 'string'
              ? baseChargeValue.toLowerCase() === 'true'
              : baseChargeValue === true;

            if (isBaseCharge && rateType !== 'PERCENTAGE') {
              return null;
            }

            return rate ? {
              categoryCode,
              rate: rate.rate,
              percentageAppliesTo: getPercentageAppliesTo(category),
            } : null;
          })
          .filter(Boolean);

        return calculateTimeOfUseBill(consumptionData, parsedKvaDemand, {
          ...options.tariffData,
          unitLines,
          demandLines,
          oilSurchargeRate: oilSurchargeRate ?? (parseFloat(options.tariffData?.oilSurchargeRate) || 0),
          serviceCharge: serviceCharge ?? (parseFloat(options.tariffData?.serviceCharge) || 0),
          includeOilSurcharge: hasOilSurcharge,
          includeServiceCharge: hasServiceCharge,
          percentageCategories,
          useUnitCostForBilling: appConfig.getBillingCapabilities().useTouUnitCost === true,
        });
      }

      if (isStandardTariffBillingMode(resolvedBillingMode)) {
        return calculateStandardTariffBill({
          consumptionData,
          kvaDemand: parsedKvaDemand,
          categories: calculationCategories,
          rates,
          ratesUsed,
          totalConsumption: effectiveTotalConsumption,
        });
      }

      // Calculate charges dynamically based on categories
      const calculatedCharges = {};
      let baseAmount = 0;
      const tariffData = options.tariffData || {};
      const timeOfUseUnits = tariffData.unitLines || consumptionData.touBreakdown || {};
      const demandBreakdown = tariffData.demandLines || consumptionData.demandBreakdown || {};
      const unitConsumptionByCategory = Object.values(timeOfUseUnits)
        .reduce((unitsByCategory, line = {}) => {
          if (line.categoryCode) {
            unitsByCategory[line.categoryCode] = parseFloat(line.units) || 0;
          }
          return unitsByCategory;
        }, {});
      const demandByCategory = Object.values(demandBreakdown)
        .reduce((demandByCategoryCode, line = {}) => {
          if (line.categoryCode) {
            demandByCategoryCode[line.categoryCode] = parseFloat(line.kw) || 0;
          }
          return demandByCategoryCode;
        }, {});
      const sortedCategories = [...calculationCategories].sort(
        (a, b) => (parseInt(a.calculation_order, 10) || 0) - (parseInt(b.calculation_order, 10) || 0),
      );

      // First pass: Calculate base charges
      for (const category of sortedCategories) {
        if (!category.is_base_charge) continue;

        const rate = rates[category.category_code];
        if (!rate) continue;

        let amount = 0;

        // Calculate based on category type - completely dynamic
        if (Object.prototype.hasOwnProperty.call(unitConsumptionByCategory, category.category_code)) {
          amount = unitConsumptionByCategory[category.category_code] * parseFloat(rate.rate);
        } else if (Object.prototype.hasOwnProperty.call(demandByCategory, category.category_code)) {
          amount = demandByCategory[category.category_code] * parseFloat(rate.rate);
        } else if (category.category_code === 'OIL_SURCHARGE') {
          amount = effectiveTotalConsumption * parseFloat(rate.rate);
        } else if (category.category_code === 'BILLING_UNIT_COST') {
          amount = effectiveTotalConsumption * parseFloat(rate.rate);
        } else if (category.category_code === 'KWH_FIXED_CHARGE') {
          // kWh consumption based charge
          amount = effectiveTotalConsumption * parseFloat(rate.rate);
        } else if (category.category_code === 'KVA_PER_UNIT_CHARGE' || category.category_code === 'KVA_DEMAND_CHARGE') {
          // kVA / HP demand based charge
          amount = parsedKvaDemand * parseFloat(rate.rate);
        } else if (category.category_code === 'SERVICE_CHARGE') {
          const serviceChargeRate = parseFloat(rate.rate) || 0;

          if (rate.rate_type === 'DAILY' || rate.rateType === 'DAILY') {
            // Daily service charge - calculate for billing period
            const { billingPeriod } = consumptionData;
            let daysInPeriod = 30; // Default fallback

            if (billingPeriod && billingPeriod.startDate && billingPeriod.endDate) {
              const startDate = new Date(billingPeriod.startDate);
              const endDate = new Date(billingPeriod.endDate);
              daysInPeriod = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            }

            amount = serviceChargeRate * daysInPeriod;
          } else {
            // Fixed service charge - use the rate directly
            amount = serviceChargeRate;
          }
        } else {
          // Fixed amount charges
          amount = parseFloat(rate.rate);
        }

        // Ensure amount is a valid number
        if (isNaN(amount)) {
          console.warn(`⚠️ NaN detected for category ${category.category_code}:`, {
            consumption: consumptionData.totalConsumption,
            kvaDemand,
            rate: rate.rate,
          });
          amount = 0;
        }

        calculatedCharges[category.category_code] = amount;
        baseAmount += amount;
      }

      // Second pass: Calculate percentage-based charges
      for (const category of calculationCategories) {
        if (category.is_base_charge) continue;

        const rate = rates[category.category_code];
        if (!rate) continue;

        let baseAmountForPercentage = 0;
        const percentageAppliesTo = getPercentageAppliesTo(category);

        // Check if category has specific base charges it applies to
        if (percentageAppliesTo.length) {
          // Calculate percentage based on specific base charges
          for (const baseCategory of percentageAppliesTo) {
            if (calculatedCharges[baseCategory]) {
              baseAmountForPercentage += parseFloat(calculatedCharges[baseCategory]) || 0;
            }
          }
        } else {
          // Fallback to total base amount (existing behavior)
          baseAmountForPercentage = baseAmount;
        }

        const amount = (parseFloat(baseAmountForPercentage) * parseFloat(rate.rate)) / 100;

        // Ensure amount is a valid number
        if (isNaN(amount)) {
          console.warn(`⚠️ NaN detected for percentage category ${category.category_code}:`, {
            baseAmountForPercentage,
            rate: rate.rate,
          });
          calculatedCharges[category.category_code] = 0;
        } else {
          calculatedCharges[category.category_code] = amount;
        }
      }

      // Calculate total
      const totalCalculated = Object.values(calculatedCharges).reduce((sum, amount) => {
        const numAmount = parseFloat(amount) || 0;
        return sum + numAmount;
      }, 0);

      // Add totalCalculated to the charges object
      calculatedCharges.totalCalculated = totalCalculated;

      return {
        calculatedCharges,
        ratesUsed,
        subtotal: baseAmount,
        calculationMode: resolvedBillingMode,
      };
    } catch (error) {
      console.error('❌ Error calculating bill:', error);
      throw error;
    }
  }
}

/**
 * Real ShopWorx Integration Service
 * Integrates with actual ShopWorx APIs for consumption data
 */
export class RealShopWorxService {
  /**
   * Gets consumption data from ShopWorx API using plant energy consumption endpoint
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<ConsumptionData>} Consumption data
   */
  static async getConsumptionData(startDate, endDate) {
    try {
      // Use the plant energy consumption API for electricity data
      const plantData = await this.getPlantEnergyConsumption(startDate, endDate, 'electricity');

      return new ConsumptionData({
        totalConsumption: plantData.totalConsumption,
        initialReading: plantData.initialReading, // Will be null in automatic mode
        finalReading: plantData.finalReading, // Will be null in automatic mode
        billingPeriod: { startDate, endDate },
        hourlyReadings: [], // Not provided by this API
        dataSource: 'automatic',
        meterName: plantData.meterName,
        availableMeters: plantData.availableMeters,
      });
    } catch (error) {
      console.error('Error fetching consumption data from plant energy API:', error);

      // Fallback to legacy method if plant API fails
      console.warn('⚠️ Plant energy API failed, trying legacy electricity API...');
      try {
        return this.getConsumptionDataFromLegacyAPI(startDate, endDate);
      } catch (fallbackError) {
        console.error('Legacy electricity API also failed:', fallbackError);

        // If both methods fail, provide helpful error message
        if (this.isNetworkError(error) || this.isNetworkError(fallbackError)) {
          throw new Error('Unable to connect to electricity data service. Please check your network connection.');
        }

        // Re-throw the original error for other types of failures
        throw error;
      }
    }
  }

  /**
   * Legacy method to get consumption data (renamed for clarity)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<ConsumptionData>} Consumption data
   */
  static async getConsumptionDataFromLegacyAPI(startDate, endDate) {
    try {
      // Try to get consumption summary from API first
      const query = {
        period_start: { $gte: DateUtils.toUTCTimestamp(startDate) },
        period_end: { $lte: DateUtils.toUTCTimestampEndOfDay(endDate) },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.CONSUMPTION_SUMMARY, query);
      const summaries = response.results || response.data || response;

      if (summaries && summaries.length > 0) {
        return this.transformConsumptionSummary(summaries[0], startDate, endDate);
      }
      return this.getConsumptionDataFromHourlyReadings(startDate, endDate);
    } catch (error) {
      console.error('Error fetching consumption summary from API:', error);
      return this.getConsumptionDataFromHourlyReadings(startDate, endDate);
    }
  }

  /**
   * Fallback method to get consumption data from hourly readings (legacy support)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<ConsumptionData>} Consumption data
   */
  static async getConsumptionDataFromHourlyReadings(startDate, endDate) {
    try {
      const query = {
        timestamp: {
          $gte: DateUtils.toUTCTimestamp(startDate),
          $lte: DateUtils.toUTCTimestamp(endDate),
        },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.CONSUMPTION_READINGS, query);
      const readings = response.results || response.data || response;

      if (readings && readings.length > 0) {
        return this.transformConsumptionData(readings, startDate, endDate);
      }
      return this.generateMockConsumptionSummary(startDate, endDate);
    } catch (error) {
      console.error('Error fetching hourly readings, generating mock summary:', error);
      return this.generateMockConsumptionSummary(startDate, endDate);
    }
  }

  /**
   * Transforms API consumption summary to ConsumptionData format
   * @param {Object} summary - Raw API consumption summary
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {ConsumptionData} Transformed consumption data
   */
  static transformConsumptionSummary(summary, startDate, endDate) {
    return new ConsumptionData({
      totalConsumption: summary.totalconsumption || summary.total_consumption, // Handle both field names
      initialReading: summary.initial_reading,
      finalReading: summary.final_reading,
      billingPeriod: { startDate, endDate },
      hourlyReadings: [], // No hourly readings needed for flat-rate billing
    });
  }

  /**
   * Transforms API consumption readings to ConsumptionData format (legacy support)
   * @param {Array} readings - Raw API readings
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {ConsumptionData} Transformed consumption data
   */
  static transformConsumptionData(readings, startDate, endDate) {
    const hourlyReadings = readings.map((reading) => ({
      timestamp: DateUtils.fromUTCTimestamp(reading.timestamp),
      kwhValue: reading.kwh_value,
      meterReading: reading.meter_reading,
    }));

    // Sort by timestamp
    hourlyReadings.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const totalConsumption = hourlyReadings.reduce((sum, reading) => sum + reading.kwhValue, 0);
    const initialReading = hourlyReadings[0]?.meterReading - hourlyReadings[0]?.kwhValue || 100000;
    const finalReading = hourlyReadings[hourlyReadings.length - 1]?.meterReading || initialReading;

    return new ConsumptionData({
      hourlyReadings: [], // Don't store hourly readings to save memory
      totalConsumption,
      initialReading,
      finalReading,
      billingPeriod: { startDate, endDate },
    });
  }

  /**
   * Generates mock consumption summary (simplified approach)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {ConsumptionData} Generated consumption data
   */
  static generateMockConsumptionSummary(startDate, endDate) {
    // Calculate period duration in days
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end.getTime() - start.getTime();
    const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));

    // Generate realistic consumption based on period length
    const dailyConsumption = 800 + (Math.random() * 400); // 800-1200 kWh per day
    const totalConsumption = Math.round(dailyConsumption * durationDays);

    // Generate meter readings
    const initialReading = 100000 + Math.round(Math.random() * 50000);
    const finalReading = initialReading + totalConsumption;

    return new ConsumptionData({
      totalConsumption,
      initialReading,
      finalReading,
      billingPeriod: { startDate, endDate },
      hourlyReadings: [], // No hourly readings for flat-rate billing
    });
  }

  /**
   * Generates mock consumption data with hourly readings (legacy support)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {ConsumptionData} Generated consumption data
   */
  static generateMockConsumptionData(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const hourlyReadings = [];

    const baseConsumption = 500;
    let currentMeterReading = 100000;

    let current = new Date(start);
    const endTime = new Date(end);
    while (current <= endTime) {
      const hour = current.getHours();

      let hourlyConsumption = baseConsumption;
      if (hour >= 8 && hour <= 18) {
        hourlyConsumption *= 1.5;
      } else if (hour >= 22 || hour <= 6) {
        hourlyConsumption *= 0.7;
      }

      hourlyConsumption *= (0.8 + Math.random() * 0.4);
      currentMeterReading += hourlyConsumption;

      hourlyReadings.push({
        timestamp: new Date(current),
        kwhValue: Math.round(hourlyConsumption),
        meterReading: Math.round(currentMeterReading),
      });

      current = new Date(current);
      current.setHours(current.getHours() + 1);
    }

    const totalConsumption = hourlyReadings.reduce((sum, reading) => sum + reading.kwhValue, 0);
    const initialReading = hourlyReadings[0]?.meterReading - hourlyReadings[0]?.kwhValue || 100000;
    const finalReading = hourlyReadings[hourlyReadings.length - 1]?.meterReading || initialReading;

    return new ConsumptionData({
      hourlyReadings: [], // Don't store hourly readings to save memory
      totalConsumption,
      initialReading,
      finalReading,
      billingPeriod: { startDate, endDate },
    });
  }

  /**
   * Gets plant energy consumption data from ShopWorx report API
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {string} meterType - 'electricity' or 'dg'
   * @returns {Promise<Object>} Consumption data with readings
   */
  static async getPlantEnergyConsumption(startDate, endDate, meterType = 'electricity') {
    try {
      // Convert dates to timestamps (milliseconds)
      const startTimestamp = DateUtils.toUTCTimestamp(startDate);
      const endTimestamp = DateUtils.toUTCTimestampEndOfDay(endDate);

      // Prepare request body
      const requestBody = {
        startdate: startTimestamp,
        enddate: endTimestamp,
      };
      // Use a custom request for this specific endpoint since it needs special parameter handling
      const url = new URL(`${API_CONFIG.BASE_URL}/server/executereport/plantenergyconsumption`);

      url.searchParams.append('all', 'false');

      // Make direct request to avoid double parameter addition
      const data = await BillingApiService.request(url.toString(), {
        method: 'POST',
        data: requestBody,
      });

      if (!data || !data.reportData) {
        throw new Error('Invalid response format from plant energy consumption API - missing reportData');
      }

      // Parse reportData if it's a JSON string (fix the parsing issue)
      let parsedData;
      try {
        parsedData = typeof data.reportData === 'string'
          ? JSON.parse(data.reportData)
          : data.reportData;
      } catch (parseError) {
        console.error('Error parsing reportData:', parseError);
        console.error('Raw reportData:', data.reportData);
        throw new Error('Invalid JSON format in reportData from plant energy consumption API');
      }

      // The parsed data should have cols and reportData fields (fix the nested structure check)
      if (!parsedData || !parsedData.reportData || !Array.isArray(parsedData.reportData)) {
        console.error('Invalid parsed data structure:', parsedData);
        throw new Error('Invalid response format from plant energy consumption API - missing or invalid nested reportData');
      }

      const { reportData } = parsedData;

      // Determine which meter to use based on type
      // Based on the actual API response, the correct meter names are:
      const meterName = meterType === 'dg' ? 'DG' : 'MAIN';

      // Find the consumption data for the specified meter
      const meterData = reportData.find((item) => item.meternumber === meterName);

      if (!meterData) {
        throw new Error(`No consumption data found for ${meterType} meter (${meterName}). Available meters: ${reportData.map((item) => item.meternumber).join(', ')}`);
      }

      const totalConsumption = parseFloat(meterData.consumption) || 0;

      // In automatic mode, we don't derive start and end readings - we only use total consumption
      return {
        totalConsumption: Math.round(totalConsumption),
        initialReading: null, // Not available in automatic mode
        finalReading: null, // Not available in automatic mode
        hasActualReadings: false, // Indicate that individual readings are not available
        meterName,
        rawResponse: data,
        parsedData,
        availableMeters: reportData.map((item) => ({
          name: item.meternumber,
          consumption: item.consumption,
        })),
        dataSource: 'automatic',
      };
    } catch (error) {
      console.error('Error fetching plant energy consumption:', error);
      throw new Error(`Failed to fetch ${meterType} consumption data: ${error.message}`);
    }
  }

  /**
   * Tests connection to ShopWorx API
   * @returns {Promise<boolean>} Connection status
   */
  static async testConnection() {
    // TODO: Replace with actual ShopWorx API health check
    return true;
  }

  /**
   * Checks if an error is network-related
   * @param {Error} error - Error to check
   * @returns {boolean} True if network error
   */
  static isNetworkError(error) {
    if (!error || !error.message) return false;

    const networkErrorPatterns = [
      'fetch',
      'network',
      'connection',
      'timeout',
      'ECONNREFUSED',
      'ENOTFOUND',
      'ETIMEDOUT',
    ];

    return networkErrorPatterns.some((pattern) => error.message.toLowerCase().includes(pattern.toLowerCase()));
  }
}

/**
 * DG Consumption Readings API Service
 * Handles retrieval of diesel generator consumption data
 */
export class DGConsumptionService {
  /**
   * Gets DG consumption data from plant energy consumption API
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<DGConsumptionData>} DG consumption data
   */
  static async getDGConsumptionData(startDate, endDate) {
    try {
      // Validate input parameters
      if (!startDate || !endDate) {
        throw new Error('Start date and end date are required');
      }

      // Use the same plant energy consumption API for DG data
      const plantData = await RealShopWorxService.getPlantEnergyConsumption(startDate, endDate, 'dg');

      return new DGConsumptionData({
        totalConsumption: plantData.totalConsumption,
        initialReading: plantData.initialReading, // Will be null in automatic mode
        finalReading: plantData.finalReading, // Will be null in automatic mode
        billingPeriod: { startDate, endDate },
        meterReadings: [], // Not provided by this API
        dataSource: 'automatic',
        meterName: plantData.meterName,
        availableMeters: plantData.availableMeters,
      });
    } catch (error) {
      console.error('Error fetching DG consumption data from plant energy API:', error);

      // Fallback to legacy method if plant API fails
      console.warn('⚠️ Plant energy API failed, trying legacy DG API...');
      try {
        return this.getDGConsumptionDataFromLegacyAPI(startDate, endDate);
      } catch (fallbackError) {
        console.error('Legacy DG API also failed:', fallbackError);

        // If both methods fail, check if it's a network issue
        if (this.isNetworkError(error) || this.isNetworkError(fallbackError)) {
          throw new Error('Unable to connect to DG data service. Please check your network connection.');
        }

        // Return null to indicate graceful degradation
        console.warn('No DG consumption data available, allowing manual entry only');
        return null;
      }
    }
  }

  /**
   * Legacy method to get DG consumption data (renamed for clarity)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<DGConsumptionData>} DG consumption data
   */
  static async getDGConsumptionDataFromLegacyAPI(startDate, endDate) {
    try {
      // Try to get DG consumption summary from API first
      const query = {
        period_start: { $gte: DateUtils.toUTCTimestamp(startDate) },
        period_end: { $lte: DateUtils.toUTCTimestampEndOfDay(endDate) },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.DG_CONSUMPTION_SUMMARY, query);
      const summaries = response.results || response.data || response;

      if (summaries && summaries.length > 0) {
        return this.transformDGConsumptionSummary(summaries[0], startDate, endDate);
      }
      console.warn('No DG consumption summary found, trying individual readings');
      return this.getDGConsumptionDataFromReadings(startDate, endDate);
    } catch (error) {
      console.error('Error fetching DG consumption summary from legacy API:', error);
      return this.getDGConsumptionDataFromReadings(startDate, endDate);
    }
  }

  /**
   * Fallback method to get DG consumption data from individual readings
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<DGConsumptionData>} DG consumption data
   */
  static async getDGConsumptionDataFromReadings(startDate, endDate) {
    try {
      const query = {
        timestamp: {
          $gte: DateUtils.toUTCTimestamp(startDate),
          $lte: DateUtils.toUTCTimestamp(endDate),
        },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.DG_CONSUMPTION_READINGS, query);
      const readings = response.results || response.data || response;

      if (readings && readings.length > 0) {
        return this.transformDGConsumptionData(readings, startDate, endDate);
      }
      console.warn('No DG readings found for the specified period');
      return this.generateMockDGConsumptionSummary(startDate, endDate);
    } catch (error) {
      console.error('Error fetching DG readings:', error);

      // Check if it's a network error
      if (this.isNetworkError(error)) {
        throw new Error('Network connection failed while fetching DG readings');
      }

      return this.generateMockDGConsumptionSummary(startDate, endDate);
    }
  }

  /**
   * Checks if an error is network-related
   * @param {Error} error - Error to check
   * @returns {boolean} True if network error
   */
  static isNetworkError(error) {
    if (!error || !error.message) return false;

    const networkErrorPatterns = [
      'fetch',
      'network',
      'connection',
      'timeout',
      'ECONNREFUSED',
      'ENOTFOUND',
      'ETIMEDOUT',
    ];

    return networkErrorPatterns.some((pattern) => error.message.toLowerCase().includes(pattern.toLowerCase()));
  }

  /**
   * Transforms API DG consumption summary to DGConsumptionData format
   * @param {Object} summary - Raw API DG consumption summary
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {DGConsumptionData} Transformed DG consumption data
   */
  static transformDGConsumptionSummary(summary, startDate, endDate) {
    return new DGConsumptionData({
      totalConsumption: summary.totalconsumption || summary.total_consumption,
      initialReading: summary.initial_reading,
      finalReading: summary.final_reading,
      billingPeriod: { startDate, endDate },
      meterReadings: [],
    });
  }

  /**
   * Transforms API DG consumption readings to DGConsumptionData format
   * @param {Array} readings - Raw API DG readings
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {DGConsumptionData} Transformed DG consumption data
   */
  static transformDGConsumptionData(readings, startDate, endDate) {
    const meterReadings = readings.map((reading) => ({
      timestamp: DateUtils.fromUTCTimestamp(reading.timestamp),
      kwhValue: reading.kwh_value,
      meterReading: reading.meter_reading,
    }));

    // Sort by timestamp
    meterReadings.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const totalConsumption = meterReadings.reduce((sum, reading) => sum + reading.kwhValue, 0);
    const initialReading = meterReadings[0]?.meterReading - meterReadings[0]?.kwhValue || 50000;
    const finalReading = meterReadings[meterReadings.length - 1]?.meterReading || initialReading;

    return new DGConsumptionData({
      totalConsumption,
      initialReading,
      finalReading,
      billingPeriod: { startDate, endDate },
      meterReadings: [],
    });
  }

  /**
   * Generates mock DG consumption summary for local development
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {DGConsumptionData} Generated DG consumption data
   */
  static generateMockDGConsumptionSummary(startDate, endDate) {
    // Calculate period duration in days
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end.getTime() - start.getTime();
    const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));

    // Generate realistic DG consumption (typically lower than main grid)
    const dailyConsumption = 200 + (Math.random() * 300); // 200-500 kWh per day for DG
    const totalConsumption = Math.round(dailyConsumption * durationDays);

    // Generate DG meter readings
    const initialReading = 50000 + Math.round(Math.random() * 25000);
    const finalReading = initialReading + totalConsumption;

    return new DGConsumptionData({
      totalConsumption,
      initialReading,
      finalReading,
      billingPeriod: { startDate, endDate },
      meterReadings: [],
    });
  }
}

/**
 * DG Billing Records API Service
 * Handles CRUD operations for DG billing records
 */
export class DGBillingRecordsService {
  /**
   * Gets all active DG billing records from API
   * @returns {Promise<DGBillingRecord[]>} Array of active DG billing records
   */
  static async getAllRecords() {
    try {
      // Only fetch active records (is_active = true or undefined for backward compatibility)
      const query = {
        $or: [
          { is_active: true },
          { is_active: { $exists: false } }, // For records created before soft delete was implemented
        ],
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS, query);
      const records = response.results || response.data || response;

      if (!Array.isArray(records)) {
        console.warn('Invalid response format for DG billing records');
        return [];
      }

      return records.map((record) => {
        try {
          return this.transformFromAPI(record);
        } catch (transformError) {
          console.error('Error transforming DG record:', transformError, record);
          return null;
        }
      }).filter((record) => record !== null);
    } catch (error) {
      console.error('Error fetching DG billing records:', error);

      // Provide more specific error messages
      if (error.message && error.message.includes('HTTP 404')) {
        throw new Error('DG billing records endpoint not found. Please contact support.');
      }

      if (error.message && error.message.includes('HTTP 403')) {
        throw new Error('Access denied to DG billing records. Please check your permissions.');
      }

      if (this.isNetworkError(error)) {
        throw new Error('Network connection failed. Please check your internet connection.');
      }

      throw new Error('Failed to load DG billing records. Please try again later.');
    }
  }

  /**
   * Gets DG billing records within a date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<DGBillingRecord[]>} Array of matching DG records
   */
  static async getRecordsByDateRange(startDate, endDate) {
    try {
      const query = {
        billing_period_start: { $gte: DateUtils.toYYYYMMDD(startDate) },
        billing_period_end: { $lte: DateUtils.toYYYYMMDD(endDate) },
      };

      const response = await BillingApiService.get(API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS, query);
      const records = response.results || response.data || response;

      return records.map((record) => this.transformFromAPI(record));
    } catch (error) {
      console.error('Error fetching DG billing records by date range:', error);
      throw error;
    }
  }

  /**
   * Creates a new DG billing record
   * @param {Object} recordData - DG billing record data
   * @returns {Promise<DGBillingRecord>} Created DG record
   */
  static async createRecord(recordData) {
    try {
      // Validate input data
      if (!recordData) {
        throw new Error('Record data is required');
      }

      const data = this.transformToAPI(recordData);
      const response = await BillingApiService.post(API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS, data);

      const createdRecord = response.results || response.data || response;

      if (!createdRecord) {
        throw new Error('No record returned from server after creation');
      }

      return this.transformFromAPI(createdRecord);
    } catch (error) {
      console.error('Error creating DG billing record:', error);

      // Provide specific error messages
      if (error.message && error.message.includes('HTTP 400')) {
        throw new Error('Invalid record data. Please check all required fields are filled correctly.');
      }

      if (error.message && error.message.includes('HTTP 409')) {
        throw new Error('A DG billing record for this period already exists.');
      }

      if (this.isNetworkError(error)) {
        throw new Error('Network connection failed while saving. Please check your connection and try again.');
      }

      // Pass through validation errors
      if (error.message && (error.message.includes('required') || error.message.includes('invalid'))) {
        throw error;
      }

      throw new Error('Failed to save DG billing record. Please try again.');
    }
  }

  /**
   * Updates an existing DG billing record
   * @param {string} id - Record ID
   * @param {Object} updateData - Updated record data
   * @returns {Promise<DGBillingRecord>} Updated DG record
   */
  static async updateRecord(id, updateData) {
    try {
      const data = {
        ...this.transformToAPI(updateData),
        updated_at: DateUtils.now(),
      };

      const objectId = updateData._id || id;
      const endpoint = `${API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS}/${objectId}`;
      const response = await BillingApiService.put(endpoint, data);

      const updatedRecord = response.results || response.data || response;
      return this.transformFromAPI(updatedRecord);
    } catch (error) {
      console.error('Error updating DG billing record:', error);
      throw error;
    }
  }

  /**
   * Gets a single DG billing record by ID
   * @param {string} id - Record ID
   * @returns {Promise<DGBillingRecord|null>} DG billing record or null if not found
   */
  static async getRecordById(id) {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS}/${id}`;
      const response = await BillingApiService.get(endpoint);

      // Handle ShopWorx API response format: { elementName, results, totalCount }
      const record = response.results || response.data || response;

      if (!record) {
        return null;
      }

      return this.transformFromAPI(record);
    } catch (error) {
      console.error('Error fetching DG billing record by ID:', error);

      // Return null for 404 errors (record not found)
      if (error.message && error.message.includes('HTTP 404')) {
        return null;
      }

      throw error;
    }
  }

  /**
   * Deletes a DG billing record (soft delete)
   * @param {string} recordId - Record ID to delete
   * @returns {Promise<boolean>} Success status
   */
  static async deleteRecord(recordId) {
    try {
      const data = {
        is_active: false,
        updated_at: DateUtils.now(),
      };

      const endpoint = `${API_CONFIG.ENDPOINTS.DG_BILLING_RECORDS}/${recordId}`;
      await BillingApiService.put(endpoint, data);
      return true;
    } catch (error) {
      console.error('Error deleting DG billing record:', error);
      throw error;
    }
  }

  /**
   * Transforms API response to application format
   * @param {Object} apiRecord - Record from API
   * @returns {DGBillingRecord} Transformed DG record
   */
  static transformFromAPI(apiRecord) {
    // Parse efficiency_metrics if it's a JSON string
    let efficiencyMetrics = {};
    if (apiRecord.efficiency_metrics) {
      try {
        efficiencyMetrics = typeof apiRecord.efficiency_metrics === 'string'
          ? JSON.parse(apiRecord.efficiency_metrics)
          : apiRecord.efficiency_metrics;
      } catch (error) {
        console.error('Error parsing efficiency_metrics:', error);
        efficiencyMetrics = {};
      }
    }

    // Handle both possible field names for DG total consumption
    const totalConsumption = parseFloat(
      apiRecord.dg_total_consumption
      || apiRecord.totalConsumption
      || 0,
    );

    // Normalize DG consumption data to ensure consistent field names
    const normalizedDGConsumptionData = {
      totalConsumption,
      initialReading: parseFloat(apiRecord.dg_initial_reading) || 0,
      finalReading: parseFloat(apiRecord.dg_final_reading) || 0,
      billingPeriod: {
        startDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_start),
        endDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_end),
      },
      meterReadings: [],
    };

    const transformedRecord = new DGBillingRecord({
      id: apiRecord.id,
      _id: apiRecord._id,
      billingPeriod: {
        startDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_start),
        endDate: DateUtils.fromYYYYMMDD(apiRecord.billing_period_end),
      },
      dgConsumptionData: normalizedDGConsumptionData,
      dieselConsumption: parseFloat(apiRecord.diesel_consumption) || 0,
      runningHours: parseFloat(apiRecord.running_hours) || 0,
      efficiencyMetrics,
      createdBy: apiRecord.created_by,
      createdAt: DateUtils.fromUTCTimestamp(apiRecord.created_at),
      updatedAt: apiRecord.updated_at ? DateUtils.fromUTCTimestamp(apiRecord.updated_at) : null,
    });
    return transformedRecord;
  }

  /**
   * Transforms application format to API format
   * @param {Object} recordData - Application DG record data
   * @returns {Object} API format data
   */
  static transformToAPI(recordData) {
    return {
      billing_period_start: DateUtils.toYYYYMMDD(recordData.billingPeriod.startDate),
      billing_period_end: DateUtils.toYYYYMMDD(recordData.billingPeriod.endDate),
      dg_total_consumption: recordData.dgConsumptionData.totalConsumption,
      dg_initial_reading: recordData.dgConsumptionData.initialReading || 0,
      dg_final_reading: recordData.dgConsumptionData.finalReading || 0,
      diesel_consumption: recordData.dieselConsumption,
      running_hours: recordData.runningHours,
      efficiency_metrics: JSON.stringify(recordData.efficiencyMetrics || {}),
      created_by: recordData.createdBy,
      created_at: DateUtils.now(),
      is_active: true, // Ensure new DG records are active by default
    };
  }

  /**
   * Checks if an error is network-related
   * @param {Error} error - Error to check
   * @returns {boolean} True if network error
   */
  static isNetworkError(error) {
    if (!error || !error.message) return false;

    const networkErrorPatterns = [
      'fetch',
      'network',
      'connection',
      'timeout',
      'ECONNREFUSED',
      'ENOTFOUND',
      'ETIMEDOUT',
    ];

    return networkErrorPatterns.some((pattern) => error.message.toLowerCase().includes(pattern.toLowerCase()));
  }
}

// Export all services
export default {
  CostCategoriesService,
  RealRateMasterService,
  RealBillingRecordsService,
  RealBillingCalculationService,
  RealShopWorxService,
  RealBusinessHoursService,
  BillingApiService,
  DGBillingRecord,
  DGBillingRecordsService,
};
